import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner } from 'react-bootstrap';

import { RELATORIO_CORES } from '../constants/relatorio-cores';
import {
  mapRelatorioHistoricoPersistido,
  RelatorioHistoricoEvento,
} from '../utils/relatorio-historico-eventos';

import { BuscarRelatorioHistorico, RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';

function formatarDataHistorico(iso: string): string {
  if (!iso) return '—';
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

interface RelatorioHistoricoModalProps {
  show: boolean;
  relatorio: RelatorioAtividadeDto | null;
  onHide: () => void;
}

export default function RelatorioHistoricoModal({ show, relatorio, onHide }: RelatorioHistoricoModalProps) {
  const [eventos, setEventos] = useState<RelatorioHistoricoEvento[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !relatorio?.id) {
      setEventos([]);
      setErro(null);
      setCarregando(false);
      return;
    }

    let cancelado = false;
    setCarregando(true);
    setErro(null);

    BuscarRelatorioHistorico(relatorio.id)
      .then((itens) => {
        if (cancelado) return;
        setEventos(mapRelatorioHistoricoPersistido(itens));
      })
      .catch(() => {
        if (cancelado) return;
        setEventos([]);
        setErro('Não foi possível carregar o histórico deste relatório.');
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [show, relatorio?.id]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header
        closeButton
        className="align-items-start"
        style={{ borderBottom: `1px solid ${RELATORIO_CORES.BORDA_CARD}` }}
      >
        <div className="flex-grow-1 me-2 overflow-hidden" style={{ minWidth: 0 }}>
          <Modal.Title
            as="div"
            className="h5 mb-0 d-flex flex-column gap-2"
            style={{ fontSize: '1.1rem' }}
          >
            <span className="d-inline-flex flex-wrap align-items-center column-gap-2 row-gap-1">
              <i
                className="bi bi-clock-history flex-shrink-0"
                style={{ color: RELATORIO_CORES.PRIMARIA }}
                aria-hidden
              />
              <span>Histórico de movimentação</span>
            </span>
            {relatorio && (
              <Badge
                bg="light"
                text="dark"
                className="fw-normal text-wrap text-break align-self-start"
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                  lineHeight: 1.35,
                }}
              >
                {relatorio.periodo} · {relatorio.defensoriaName}
              </Badge>
            )}
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: RELATORIO_CORES.BRANCO, maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {!relatorio ? (
          <p className="text-muted mb-0">Nenhum relatório selecionado.</p>
        ) : carregando ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            <span>Carregando histórico...</span>
          </div>
        ) : erro ? (
          <Alert variant="danger" className="mb-0">
            {erro}
          </Alert>
        ) : eventos.length === 0 ? (
          <p className="text-muted mb-0">Nenhuma movimentação registrada ainda para este relatório.</p>
        ) : (
          <div
            className="timeline-historico"
            style={{
              position: 'relative',
              paddingLeft: '28px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '11px',
                top: '12px',
                bottom: '12px',
                width: '2px',
                backgroundColor: RELATORIO_CORES.PRIMARIA,
                opacity: 0.4,
                borderRadius: 1,
              }}
            />
            {eventos.map((ev, idx) => (
              <div
                key={`${ev.data}-${ev.label}-${idx}`}
                style={{
                  position: 'relative',
                  paddingBottom: idx < eventos.length - 1 ? 20 : 0,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '-21px',
                    top: '4px',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: RELATORIO_CORES.PRIMARIA,
                    border: `2px solid ${RELATORIO_CORES.BRANCO}`,
                    boxShadow: `0 0 0 1px ${RELATORIO_CORES.PRIMARIA}`,
                  }}
                />
                <div style={{ wordBreak: 'break-word' }}>
                  <span className="small fw-bold text-muted" style={{ whiteSpace: 'nowrap' }}>
                    {formatarDataHistorico(ev.data)}
                  </span>
                  <div
                    className="fw-semibold mt-1 d-flex flex-wrap align-items-center gap-2"
                    style={{ color: RELATORIO_CORES.PRIMARIA }}
                  >
                    <span>{ev.label}</span>
                    {ev.badge && (
                      <Badge bg="warning" text="dark" title={ev.badge.title}>
                        {ev.badge.text}
                      </Badge>
                    )}
                  </div>
                  {ev.detalhe && <div className="small text-muted mt-1">{ev.detalhe}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: `1px solid ${RELATORIO_CORES.BORDA_CARD}` }}>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
